import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useEffect } from "react";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { t, SupportedLocale } from "@/lib/i18n";
import { getLocalizedText } from "@/lib/localizedText";
import type { Product } from "@/data/products";
import { getHomeUrl, getAllProductsUrl, getWhereToBuyUrl, getProductUrl } from "@/lib/urls";
import { getProductImageAlt, getProductImageList, getProductImageSrc } from "@/lib/productImages";
import { getProductOffers } from "@/data/productOffers";
import { ProductImageGallery } from "@/components/ProductImageGallery";
import { buildProductPageTitle, isRichHtml, prepareRichAboutHtml } from "@/lib/productRichContent";
import { buildProductSeoDescription, buildProductSeoTitle } from "@/lib/productSeo";
import { ProductFeaturesList } from "@/components/ProductFeaturesList";
import { ProductTopOffers } from "@/components/ProductTopOffers";
import { ProseText } from "@/components/ProseText";
import { trackBuyCta, trackProductView } from "@/lib/analytics";
import styles from "@/styles/Home.module.css";
const PRODUCT_TOP_OFFERS_ID = "product-top-offers";
const BASE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://12gobricks.com";
type ProductDetailContentProps = {
  product: Product;
  locale: SupportedLocale;
};
export function ProductDetailContent(t0) {
  const $ = _c(186);
  const {
    product,
    locale
  } = t0;
  const brandName = product.brand || "12GO";
  const displayName = product.name ?? `${brandName} ${product.numbering}`;
  let t1;
  let t2;
  if ($[0] !== product.id) {
    t1 = () => {
      trackProductView(product.id);
    };
    t2 = [product.id];
    $[0] = product.id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== product.name || $[4] !== product.numbering) {
    t3 = getProductImageAlt(product.name, product.numbering);
    $[3] = product.name;
    $[4] = product.numbering;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const imageAlt = t3;
  let aboutIsRich;
  let aboutText;
  let historicalText;
  let t4;
  if ($[6] !== locale || $[7] !== product.about || $[8] !== product.historicalBackground) {
    aboutText = getLocalizedText(product.about, locale);
    let t5;
    if ($[13] !== locale || $[14] !== product.historicalBackground) {
      t5 = getLocalizedText(product.historicalBackground, locale);
      $[13] = locale;
      $[14] = product.historicalBackground;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    historicalText = t5;
    aboutIsRich = aboutText ? isRichHtml(aboutText) : false;
    t4 = aboutIsRich && aboutText ? prepareRichAboutHtml(aboutText) : null;
    $[6] = locale;
    $[7] = product.about;
    $[8] = product.historicalBackground;
    $[9] = aboutIsRich;
    $[10] = aboutText;
    $[11] = historicalText;
    $[12] = t4;
  } else {
    aboutIsRich = $[9];
    aboutText = $[10];
    historicalText = $[11];
    t4 = $[12];
  }
  const richPrepared = t4;
  const richBodyHtml = richPrepared?.bodyHtml ?? "";
  let t5;
  if ($[16] !== product || $[17] !== richPrepared?.pageTitle) {
    t5 = richPrepared?.pageTitle ?? buildProductPageTitle(product);
    $[16] = product;
    $[17] = richPrepared?.pageTitle;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const pageTitle = t5;
  let t6;
  if ($[19] !== locale || $[20] !== product) {
    t6 = buildProductSeoTitle(product, locale);
    $[19] = locale;
    $[20] = product;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const metaTitle = t6;
  let t7;
  if ($[22] !== locale || $[23] !== product) {
    t7 = buildProductSeoDescription(product, locale);
    $[22] = locale;
    $[23] = product;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const metaDesc = t7;
  let t8;
  if ($[25] !== product.id) {
    t8 = getProductImageList(product.id).map(_temp);
    $[25] = product.id;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const productImageUrls = t8;
  let t9;
  if ($[27] !== locale || $[28] !== product) {
    t9 = getProductUrl(product, locale);
    $[27] = locale;
    $[28] = product;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const productPath = t9;
  let t10;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = BASE_URL.replace(/\/+$/, "");
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const productPageUrl = `${t10}${productPath}`;
  let t11;
  if ($[31] !== product.blockSize || $[32] !== product.productSize) {
    t11 = product.productSize?.trim() || (product.blockSize && product.blockSize !== "normal" ? product.blockSize : undefined);
    $[31] = product.blockSize;
    $[32] = product.productSize;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const productSize = t11;
  let t12;
  if ($[34] !== product.id) {
    t12 = getProductOffers(product.id);
    $[34] = product.id;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const hasTopOffers = t12.length > 0;
  let t13;
  if ($[36] !== brandName) {
    t13 = {
      "@type": "Brand",
      name: brandName
    };
    $[36] = brandName;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== product.numbering) {
    t14 = {
      "@type": "PropertyValue",
      name: "Catalog number",
      value: product.numbering
    };
    $[38] = product.numbering;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== brandName) {
    t15 = {
      "@type": "PropertyValue",
      name: "Manufacturer",
      value: brandName
    };
    $[40] = brandName;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== product.pieces) {
    t16 = {
      "@type": "PropertyValue",
      name: "Pieces",
      value: product.pieces
    };
    $[42] = product.pieces;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== product.scale) {
    t17 = product.scale?.trim() ? [{
      "@type": "PropertyValue",
      name: "Scale",
      value: product.scale.trim()
    }] : [];
    $[44] = product.scale;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== productSize) {
    t18 = productSize ? [{
      "@type": "PropertyValue",
      name: "Product size",
      value: productSize
    }] : [];
    $[46] = productSize;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== product.packageSize) {
    t19 = product.packageSize?.trim() ? [{
      "@type": "PropertyValue",
      name: "Package size",
      value: product.packageSize.trim()
    }] : [];
    $[48] = product.packageSize;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== product.recommendAge) {
    t20 = {
      "@type": "PropertyValue",
      name: "Age rating",
      value: product.recommendAge
    };
    $[50] = product.recommendAge;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      "@type": "PropertyValue",
      name: "Material",
      value: "ABS plastic"
    };
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t14 || $[54] !== t15 || $[55] !== t16 || $[56] !== t17 || $[57] !== t18 || $[58] !== t19 || $[59] !== t20) {
    t22 = [t14, t15, t16, ...t17, ...t18, ...t19, t20, t21];
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
    $[58] = t19;
    $[59] = t20;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== displayName || $[62] !== metaDesc || $[63] !== product.numbering || $[64] !== productImageUrls || $[65] !== productPageUrl || $[66] !== t13 || $[67] !== t22) {
    t23 = {
      "@context": "https://schema.org",
      "@type": "Thing",
      name: displayName,
      description: metaDesc,
      image: productImageUrls,
      url: productPageUrl,
      sku: product.numbering,
      brand: t13,
      copyrightNotice: "Images \xA9 12gobricks.com",
      additionalProperty: t22
    };
    $[61] = displayName;
    $[62] = metaDesc;
    $[63] = product.numbering;
    $[64] = productImageUrls;
    $[65] = productPageUrl;
    $[66] = t13;
    $[67] = t22;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  const productJsonLd = t23;
  let t24;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = /\/+$/;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  const base = BASE_URL.replace(t24, "");
  const productsPath = getAllProductsUrl(locale);
  let t25;
  if ($[70] !== locale) {
    t25 = t(locale, "nav.home");
    $[70] = locale;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  const t26 = base + getHomeUrl(locale);
  let t27;
  if ($[72] !== t25 || $[73] !== t26) {
    t27 = {
      "@type": "ListItem",
      position: 1,
      name: t25,
      item: t26
    };
    $[72] = t25;
    $[73] = t26;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== locale) {
    t28 = t(locale, "nav.products");
    $[75] = locale;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  const t29 = base + productsPath;
  let t30;
  if ($[77] !== t28 || $[78] !== t29) {
    t30 = {
      "@type": "ListItem",
      position: 2,
      name: t28,
      item: t29
    };
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== displayName || $[81] !== productPageUrl) {
    t31 = {
      "@type": "ListItem",
      position: 3,
      name: displayName,
      item: productPageUrl
    };
    $[80] = displayName;
    $[81] = productPageUrl;
    $[82] = t31;
  } else {
    t31 = $[82];
  }
  let t32;
  if ($[83] !== t27 || $[84] !== t30 || $[85] !== t31) {
    t32 = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [t27, t30, t31]
    };
    $[83] = t27;
    $[84] = t30;
    $[85] = t31;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  const breadcrumbJsonLd = t32;
  let t33;
  if ($[87] !== product.id) {
    t33 = getProductImageSrc(product.id);
    $[87] = product.id;
    $[88] = t33;
  } else {
    t33 = $[88];
  }
  let t34;
  if ($[89] !== breadcrumbJsonLd || $[90] !== productJsonLd) {
    t34 = [productJsonLd, breadcrumbJsonLd];
    $[89] = breadcrumbJsonLd;
    $[90] = productJsonLd;
    $[91] = t34;
  } else {
    t34 = $[91];
  }
  let t35;
  if ($[92] !== metaDesc || $[93] !== metaTitle || $[94] !== productPath || $[95] !== t33 || $[96] !== t34) {
    t35 = <SEO title={metaTitle} description={metaDesc} image={t33} canonicalPath={productPath} jsonLd={t34} />;
    $[92] = metaDesc;
    $[93] = metaTitle;
    $[94] = productPath;
    $[95] = t33;
    $[96] = t34;
    $[97] = t35;
  } else {
    t35 = $[97];
  }
  let t36;
  if ($[98] !== locale) {
    t36 = t(locale, "nav.home");
    $[98] = locale;
    $[99] = t36;
  } else {
    t36 = $[99];
  }
  let t37;
  if ($[100] !== locale) {
    t37 = getHomeUrl(locale);
    $[100] = locale;
    $[101] = t37;
  } else {
    t37 = $[101];
  }
  let t38;
  if ($[102] !== t36 || $[103] !== t37) {
    t38 = {
      label: t36,
      href: t37
    };
    $[102] = t36;
    $[103] = t37;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  let t39;
  if ($[105] !== locale) {
    t39 = t(locale, "nav.products");
    $[105] = locale;
    $[106] = t39;
  } else {
    t39 = $[106];
  }
  let t40;
  if ($[107] !== locale) {
    t40 = getAllProductsUrl(locale);
    $[107] = locale;
    $[108] = t40;
  } else {
    t40 = $[108];
  }
  let t41;
  if ($[109] !== t39 || $[110] !== t40) {
    t41 = {
      label: t39,
      href: t40
    };
    $[109] = t39;
    $[110] = t40;
    $[111] = t41;
  } else {
    t41 = $[111];
  }
  let t42;
  if ($[112] !== displayName) {
    t42 = {
      label: displayName
    };
    $[112] = displayName;
    $[113] = t42;
  } else {
    t42 = $[113];
  }
  let t43;
  if ($[114] !== t38 || $[115] !== t41 || $[116] !== t42) {
    t43 = [t38, t41, t42];
    $[114] = t38;
    $[115] = t41;
    $[116] = t42;
    $[117] = t43;
  } else {
    t43 = $[117];
  }
  let t44;
  if ($[118] !== locale || $[119] !== t43) {
    t44 = <Breadcrumbs items={t43} locale={locale} />;
    $[118] = locale;
    $[119] = t43;
    $[120] = t44;
  } else {
    t44 = $[120];
  }
  let t45;
  if ($[121] !== imageAlt || $[122] !== product.id) {
    t45 = <ProductImageGallery productId={product.id} imageAlt={imageAlt} />;
    $[121] = imageAlt;
    $[122] = product.id;
    $[123] = t45;
  } else {
    t45 = $[123];
  }
  const t46 = `brand.${product.brand}`;
  let t47;
  if ($[124] !== locale || $[125] !== t46) {
    t47 = t(locale, t46);
    $[124] = locale;
    $[125] = t46;
    $[126] = t47;
  } else {
    t47 = $[126];
  }
  let t48;
  if ($[127] !== t47) {
    t48 = <span className={styles.productTag}>{t47}</span>;
    $[127] = t47;
    $[128] = t48;
  } else {
    t48 = $[128];
  }
  const t49 = `category.${product.category}`;
  let t50;
  if ($[129] !== locale || $[130] !== t49) {
    t50 = t(locale, t49);
    $[129] = locale;
    $[130] = t49;
    $[131] = t50;
  } else {
    t50 = $[131];
  }
  let t51;
  if ($[132] !== t50) {
    t51 = <span className={styles.productTag}>{t50}</span>;
    $[132] = t50;
    $[133] = t51;
  } else {
    t51 = $[133];
  }
  let t52;
  if ($[134] !== locale || $[135] !== product.category || $[136] !== product.militarySubcategory) {
    t52 = product.category === "military" && product.militarySubcategory && <span className={styles.productTagSubcategory}>{t(locale, `militarySub.${product.militarySubcategory}`)}</span>;
    $[134] = locale;
    $[135] = product.category;
    $[136] = product.militarySubcategory;
    $[137] = t52;
  } else {
    t52 = $[137];
  }
  let t53;
  if ($[138] !== locale || $[139] !== product.category || $[140] !== product.faction) {
    t53 = product.category === "military" && product.faction && <span className={styles.productTagFaction}>{t(locale, `faction.${product.faction}`)}</span>;
    $[138] = locale;
    $[139] = product.category;
    $[140] = product.faction;
    $[141] = t53;
  } else {
    t53 = $[141];
  }
  let t54;
  if ($[142] !== t48 || $[143] !== t51 || $[144] !== t52 || $[145] !== t53) {
    t54 = <div className={`${styles.productTags} ${styles.productDetailTags}`}>{t48}{t51}{t52}{t53}</div>;
    $[142] = t48;
    $[143] = t51;
    $[144] = t52;
    $[145] = t53;
    $[146] = t54;
  } else {
    t54 = $[146];
  }
  const t55 = aboutIsRich ? pageTitle : displayName;
  let t56;
  if ($[147] !== t55) {
    t56 = <h1 className={`${styles.productTitle} ${styles.productDetailTitle}`}>{t55}</h1>;
    $[147] = t55;
    $[148] = t56;
  } else {
    t56 = $[148];
  }
  let t57;
  if ($[149] !== locale || $[150] !== product) {
    t57 = <ProductFeaturesList product={product} locale={locale} />;
    $[149] = locale;
    $[150] = product;
    $[151] = t57;
  } else {
    t57 = $[151];
  }
  let t58;
  if ($[152] !== displayName || $[153] !== hasTopOffers || $[154] !== locale || $[155] !== product.id) {
    t58 = hasTopOffers ? <a href={`#${PRODUCT_TOP_OFFERS_ID}`} className={`${styles.productCta} ${styles.productDetailCta}`} onClick={event => {
      trackBuyCta(product.id);
      const target = document.getElementById(PRODUCT_TOP_OFFERS_ID);
      if (!target) {
        return;
      }
      event.preventDefault();
      target.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      if (typeof window !== "undefined") {
        window.history.replaceState(null, "", `#${PRODUCT_TOP_OFFERS_ID}`);
      }
    }}>{t(locale, "product.buyButton").replace("{name}", displayName)}</a> : <Link href={getWhereToBuyUrl(locale)} className={`${styles.productCta} ${styles.productDetailCta}`} onClick={() => trackBuyCta(product.id)}>{t(locale, "product.buyButton").replace("{name}", displayName)}</Link>;
    $[152] = displayName;
    $[153] = hasTopOffers;
    $[154] = locale;
    $[155] = product.id;
    $[156] = t58;
  } else {
    t58 = $[156];
  }
  let t59;
  if ($[157] !== t54 || $[158] !== t56 || $[159] !== t57 || $[160] !== t58) {
    t59 = <div className={styles.productDetailInfo}>{t54}{t56}{t57}{t58}</div>;
    $[157] = t54;
    $[158] = t56;
    $[159] = t57;
    $[160] = t58;
    $[161] = t59;
  } else {
    t59 = $[161];
  }
  let t60;
  if ($[162] !== t45 || $[163] !== t59) {
    t60 = <div className={styles.productDetailGrid}>{t45}{t59}</div>;
    $[162] = t45;
    $[163] = t59;
    $[164] = t60;
  } else {
    t60 = $[164];
  }
  let t61;
  if ($[165] !== displayName || $[166] !== locale || $[167] !== product.id) {
    t61 = <ProductTopOffers productId={product.id} productName={displayName} locale={locale} />;
    $[165] = displayName;
    $[166] = locale;
    $[167] = product.id;
    $[168] = t61;
  } else {
    t61 = $[168];
  }
  let t62;
  if ($[169] !== aboutIsRich || $[170] !== aboutText || $[171] !== displayName || $[172] !== historicalText || $[173] !== locale || $[174] !== richBodyHtml) {
    t62 = (aboutText || historicalText) && <div className={styles.productDetailBody}><h2 className={styles.productDescriptionHeading}>{t(locale, "product.descriptionTitle").replace("{name}", displayName)}</h2>{aboutText && <div className={`${styles.productAbout} ${styles.productDetailSection}`}>{aboutIsRich ? <div className={`${styles.productRichContent} prose-content`} dangerouslySetInnerHTML={{
          __html: richBodyHtml
        }} /> : <ProseText text={aboutText} />}</div>}{historicalText && <div className={`${styles.productHistorical} ${styles.productDetailSection}`}><h2 className={styles.productSectionTitle}>{t(locale, "product.historicalTitle")}</h2><ProseText text={historicalText} /></div>}</div>;
    $[169] = aboutIsRich;
    $[170] = aboutText;
    $[171] = displayName;
    $[172] = historicalText;
    $[173] = locale;
    $[174] = richBodyHtml;
    $[175] = t62;
  } else {
    t62 = $[175];
  }
  let t63;
  if ($[176] !== t60 || $[177] !== t61 || $[178] !== t62) {
    t63 = <div className={styles.productDetailCard}>{t60}{t61}{t62}</div>;
    $[176] = t60;
    $[177] = t61;
    $[178] = t62;
    $[179] = t63;
  } else {
    t63 = $[179];
  }
  let t64;
  if ($[180] !== t44 || $[181] !== t63) {
    t64 = <Layout><main className={styles.main}><section className={`${styles.sectionBestsellers} ${styles.sectionProductDetail}`}><div className={styles.sectionInner}><div className={styles.productDetailWrap}>{t44}{t63}</div></div></section></main></Layout>;
    $[180] = t44;
    $[181] = t63;
    $[182] = t64;
  } else {
    t64 = $[182];
  }
  let t65;
  if ($[183] !== t35 || $[184] !== t64) {
    t65 = <div className={styles.page}>{t35}{t64}</div>;
    $[183] = t35;
    $[184] = t64;
    $[185] = t65;
  } else {
    t65 = $[185];
  }
  return t65;
}
function _temp(src) {
  return `${BASE_URL.replace(/\/+$/, "")}${src}`;
}